/**
 * @file confirm-email.component.ts
 * @description
 * This component handles the email confirmation process for the Superadmin Panel.
 * It verifies the email confirmation token sent to the user's registered email.
 *
 * The component includes the following:
 * 1. Extracts the email confirmation userid from the URL.
 * 2. API integration to validate the confirmation userid and password to activate the user account.
 * 3. Displays success or error messages based on the verification response.
 * 4. Provides navigation options to:
 *    - Redirect the user to the login page upon successful confirmation.
 *    - Show error messages if the token is invalid or expired.
 *
 * @dependencies:
 * - Authentication Service for handling email confirmation requests.
 * - Angular Router (`@angular/router`) for extracting URL parameters and navigation.
 * - Angular Material Snackbar (`MatSnackBar`) for displaying success and error messages.
 *
 * @param {MatSnackBar} snackBar - Used to display notifications for success and error messages.
 */


import { Component } from '@angular/core';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PasswordValidator } from '../../../validators/custom-validators';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormValidation } from '../../../shared/services/utils/form-validation.service';
import { ApiService } from '../../../shared/services/api.services';
import { superAdminHttpVersion } from '../../../consts/super_admin_http_routes';
import { encryptData, showNotification } from '../../../consts/utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SeoService } from '../../../shared/services/seo.service';

@Component({
  selector: 'app-confirm-email',
  imports: [LoaderComponent, RouterModule, ReactiveFormsModule],
  templateUrl: './confirm-email.component.html',
  styleUrl: './confirm-email.component.scss'
})

export class ConfirmEmailComponent {
  public show: boolean = true;
  public showConfirm: boolean = true;
  public isLoading: boolean = false;

  private userId: string;
  private email: string;
  private token: string;


  form: UntypedFormGroup;
  public validate = false;

  constructor(
    private apiService: ApiService,
    private _formBuilder: UntypedFormBuilder,
    private _router: Router,
    private activatedRoute: ActivatedRoute,
    public formValidation: FormValidation,
    private snackBar: MatSnackBar,
    private seo: SeoService,
  ) {
    /**
        * Sets up SEO meta tags using SeoService
        * This helps in optimizing the page for search engines and social media previews.
    */
    this.seo.setTags({
      title: 'SuperAdmin Confirm Email Form', // Title
      titleSuffix: '- Shining On Safety', // Title Suffix
      description: 'Shining On Safety', // Description
      image: '', // Image
      keywords: 'Shining On Safety' // Keywords
    });
  }

  ngOnInit(): void {
    this.form = this._formBuilder.group({
      password: [
        '',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(50),
            Validators.minLength(8),
          ]),
        ],
      ],
      confirmPassword: [
        '',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(50),
            Validators.minLength(8),
          ]),
        ],
      ],
    }, {
      validators: [PasswordValidator.matchPassword]
    });

    this.activatedRoute.queryParams.subscribe(params => {
      // this.userId = params['userId'];
      this.email = params['email'];
      this.token = params['token'];

    });
  }

  /**
   * Confirm email api call, which accepts the userId and Password to activate user account
   */

  activateAccount() {
    this.validate = true;
    if (this.form.invalid) {
      showNotification(this.snackBar, 'Please check the form fields', 'error');
      return;
    }

    this.isLoading = true;

    const encryptedPassword = encryptData(this.form.get('password')?.value);
    const encryptedConfirmPassword = encryptData(this.form.get('confirmPassword')?.value);
    const encryptedUserId = encryptData(this.userId);
    const encryptedToken = this.token;
    const encryptedEmail = encryptData(this.email);


    var data = {
      password: encryptedPassword,
      confirmPassword: encryptedConfirmPassword,
      // userId: encryptedUserId,
      token: encryptedToken,
      email: encryptedEmail
    };

    this.apiService.httpPatchCallWithoutToken<any>(
      superAdminHttpVersion.V1 + 'superadmin/confirm',
      data
    ).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.status) {
          showNotification(this.snackBar, response.message, 'success');
          this._router.navigate(['/login']);
        } else {
          showNotification(this.snackBar, response.message, 'error');
        }
      }, error: (error: any) => {
        this.isLoading = false;
        showNotification(this.snackBar, error.message, 'error');
      }
    });
  }
}
